import { useLayoutEffect, useMemo, useState } from 'react';
import { methodExists } from 'Utils/functions';
import { css } from 'styled-components';

/* eslint-disable sort-keys-fix/sort-keys-fix */
const breakpoints = Object.freeze({
  xs: '0',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1400px',
});
/* eslint-enable sort-keys-fix/sort-keys-fix */

/**
 * @typedef { keyof typeof breakpoints } Breakpoint
 */

export const matchMedia = (minWidth, maxWidth) => {
  let matchMin = true;
  let matchMax = true;
  if (minWidth) {
    matchMin = window.matchMedia(`(min-width: ${minWidth})`).matches;
  }
  if (maxWidth) {
    matchMax = window.matchMedia(`(max-width: ${maxWidth})`).matches;
  }

  return matchMin && matchMax;
};

export function useMediaQuery(query) {
  const media = useMemo(() => window.matchMedia(query), [query]);
  const [matches, setMatches] = useState(media.matches);

  useLayoutEffect(() => {
    const handler = ({ matches: currentlyMatching }) => setMatches(currentlyMatching);

    if (methodExists(media, 'addEventListener')) media.addEventListener('change', handler);
    else if (methodExists(media, 'addListener')) media.addListener(handler);

    return () => {
      if (methodExists(media, 'removeEventListener')) media.removeEventListener('change', handler);
      else if (methodExists(media, 'removeListener')) media.removeListener(handler);
    };
  }, [media]);

  return matches;
}

export const useMobileMediaQuery = () => useMediaQuery(`(max-width: ${breakpoints.md})`);
export const useDesktopXLMediaQuery = () => !useMediaQuery(`(max-width: ${breakpoints.xl})`);

/**
 * Helper media matcher for mobile first
 * @param { Breakpoint } breakpoint
 */
export const useMedia = breakpoint => useMediaQuery(`(min-width: ${breakpoints[breakpoint]})`);

const wrapResponsive = (args, size, min = false) => (
  css`@media only screen and (${min ? 'min' : 'max'}-width: ${min ? size : `calc(${size} - 1px)`}) {
    ${css(...args)};
  }`
);

/* eslint-disable sort-keys-fix/sort-keys-fix */
/**
 * `@media only screen and (max-width: ${size})`
 */
export const respondTo = {
  small: (...args) => wrapResponsive(args, breakpoints.sm),
  medium: (...args) => wrapResponsive(args, breakpoints.md),
  large: (...args) => wrapResponsive(args, breakpoints.lg),
  extralarge: (...args) => wrapResponsive(args, breakpoints.xl),
  minLarge: (...args) => wrapResponsive(args, breakpoints.xxl),
  custom: customPx => (...args) => wrapResponsive(args, `${customPx}px`),
};
/**
 * `@media only screen and (min-width: ${size})`
 */
export const mediaUp = {
  small: (...args) => css(...args),
  medium: (...args) => wrapResponsive(args, breakpoints.sm, true),
  large: (...args) => wrapResponsive(args, breakpoints.md, true),
  extralarge: (...args) => wrapResponsive(args, breakpoints.lg, true),
  custom: customPx => (...args) => wrapResponsive(args, `${customPx}px`),
};

/**
 * Media utils for mobile-first
 */
export const media = {
  small: (...args) => wrapResponsive(args, breakpoints.sm, true),
  medium: (...args) => wrapResponsive(args, breakpoints.md, true),
  large: (...args) => wrapResponsive(args, breakpoints.lg, true),
  extralarge: (...args) => wrapResponsive(args, breakpoints.xl, true),
  extraExtraLarge: (...args) => wrapResponsive(args, breakpoints.xxl, true),
};
/* eslint-enable sort-keys-fix/sort-keys-fix */

export default {
  breakpoints,
  matchMedia,
  mediaUp,
  respondTo,
  useDesktopXLMediaQuery,
  useMedia,
  useMediaQuery,
  useMobileMediaQuery,
};
